import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CONTACT } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  company: "",
  profile: "entreprise",
  interest: "vie",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de renseigner nom, e-mail et message.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.phone) delete payload.phone;
      if (!payload.company) delete payload.company;
      await axios.post(`${API}/contact`, payload);
      toast.success("Votre demande a bien été enregistrée. Nous revenons vers vous sous 24 h ouvrées.", {
        duration: 6000,
        className: "font-sans",
      });
      setForm(INITIAL);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Une erreur est survenue. Merci de réessayer ou de nous joindre directement.";
      toast.error(typeof msg === "string" ? msg : "Erreur d’envoi.");
    } finally {
      setLoading(false);
    }
  };

  const inputClasses =
    "rounded-none border-[#0a192f]/20 bg-white h-12 px-4 focus-visible:ring-1 focus-visible:ring-[#0a192f] focus-visible:border-[#0a192f] font-sans text-[#0a192f]";

  return (
    <section
      id="contact"
      data-testid={CONTACT.root}
      className="relative py-24 md:py-32 bg-[#f7f5f0]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left copy */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#c05c3d]" />
              <span className="overline">Prenons contact</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] text-[#0a192f]">
              Un audit.<br />
              <span className="italic text-[#c05c3d]">Aucun engagement.</span>
            </h2>
            <p className="mt-6 text-[#475569] leading-relaxed text-lg max-w-md">
              Décrivez-nous en quelques lignes votre situation. Un associé
              reviendra vers vous sous 24 heures ouvrées.
            </p>

            <div className="mt-12 space-y-6">
              <div className="flex items-start gap-4">
                <Mail size={18} strokeWidth={1.5} className="mt-1 text-[#c05c3d]" />
                <div>
                  <div className="overline mb-1">E-mail</div>
                  <a href="mailto:a.rechad@phenix-assurances.com" className="text-[#0a192f] hover:text-[#c05c3d] transition-colors">
                    a.rechad@phenix-assurances.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone size={18} strokeWidth={1.5} className="mt-1 text-[#c05c3d]" />
                <div>
                  <div className="overline mb-1">Téléphone</div>
                  <a href="tel:+33650347450" className="text-[#0a192f] hover:text-[#c05c3d] transition-colors">
                    +33 6 50 34 74 50
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin size={18} strokeWidth={1.5} className="mt-1 text-[#c05c3d]" />
                <div>
                  <div className="overline mb-1">Cabinet</div>
                  <div className="text-[#0a192f]">
                    30 avenue Maréchal Foch
                    <br />
                    69006 Lyon
                  </div>
                </div>
              </div>
              <div className="pt-2 border-t border-[#0a192f]/10">
                <div className="overline mb-1">Immatriculation</div>
                <div className="text-sm text-[#475569]">
                  ORIAS n° <span className="text-[#0a192f] font-medium">26005580</span>
                </div>
              </div>

              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/lead-magnet`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-lead-magnet"
                className="mt-4 group inline-flex items-center gap-3 border border-[#0a192f]/70 text-[#0a192f] px-5 py-3 text-xs tracking-[0.22em] uppercase hover:bg-[#0a192f] hover:text-[#efeade] transition-colors"
              >
                Télécharger notre livre blanc
                <ArrowUpRight
                  size={14}
                  strokeWidth={1.5}
                  className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </a>
            </div>
          </div>

          {/* Right form */}
          <form
            data-testid={CONTACT.form}
            onSubmit={handleSubmit}
            className="lg:col-span-7 bg-white border border-[#0a192f]/10 p-8 md:p-12 space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="overline">Nom complet *</Label>
                <Input
                  id="name"
                  data-testid={CONTACT.inputName}
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className={inputClasses}
                  placeholder="Jean Dupont"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="overline">E-mail *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid={CONTACT.inputEmail}
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className={inputClasses}
                  placeholder="jean.dupont@societe.fr"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="overline">Téléphone</Label>
                <Input
                  id="phone"
                  data-testid={CONTACT.inputPhone}
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className={inputClasses}
                  placeholder="+33 6 00 00 00 00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company" className="overline">Société</Label>
                <Input
                  id="company"
                  data-testid={CONTACT.inputCompany}
                  value={form.company}
                  onChange={(e) => update("company", e.target.value)}
                  className={inputClasses}
                  placeholder="Nom de la société"
                />
              </div>

              <div className="space-y-2">
                <Label className="overline">Vous êtes</Label>
                <Select
                  value={form.profile}
                  onValueChange={(v) => update("profile", v)}
                >
                  <SelectTrigger
                    data-testid={CONTACT.selectProfile}
                    className="rounded-none border-[#0a192f]/20 bg-white h-12 px-4 focus:ring-1 focus:ring-[#0a192f] text-[#0a192f]"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="entreprise">Entreprise / Dirigeant</SelectItem>
                    <SelectItem value="particulier">Particulier</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="overline">Sujet</Label>
                <Select
                  value={form.interest}
                  onValueChange={(v) => update("interest", v)}
                >
                  <SelectTrigger
                    data-testid={CONTACT.selectInterest}
                    className="rounded-none border-[#0a192f]/20 bg-white h-12 px-4 focus:ring-1 focus:ring-[#0a192f] text-[#0a192f]"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="vie">Assurance vie</SelectItem>
                    <SelectItem value="per">PER / Retraite</SelectItem>
                    <SelectItem value="prevoyance">Prévoyance / Santé</SelectItem>
                    <SelectItem value="ird">IRD / Entreprise</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="overline">Votre message *</Label>
              <Textarea
                id="message"
                data-testid={CONTACT.inputMessage}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                rows={5}
                className="rounded-none border-[#0a192f]/20 bg-white focus-visible:ring-1 focus-visible:ring-[#0a192f] focus-visible:border-[#0a192f] text-[#0a192f] resize-none"
                placeholder="Contexte, objectifs, questions particulières…"
                required
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
              <p className="text-xs text-[#475569] max-w-sm leading-relaxed">
                Vos données sont traitées confidentiellement — cabinet immatriculé
                ORIAS n° 26005580. Aucune diffusion à des tiers.
              </p>
              <button
                type="submit"
                data-testid={CONTACT.submit}
                disabled={loading}
                className="group inline-flex items-center gap-3 bg-[#0a192f] text-[#efeade] px-8 py-4 text-sm tracking-wide hover:bg-[#c05c3d] transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Envoi en cours…
                  </>
                ) : (
                  <>
                    Envoyer ma demande
                    <ArrowUpRight
                      size={18}
                      strokeWidth={1.5}
                      className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                    />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
